import * as vp from 'vopay';
import { ChangeEvent, JSX, useState } from 'react';
import moment from 'moment';
import { TransferFundRequest } from './types';
import styles from './TransferFund.module.scss';
import { useTranslation } from 'react-i18next';
import { FormikHelpers } from 'formik';

type SelectOption = vp.SelectOption<vp.ClientAccount>;

export type TransferFundRows = {
  label: string;
  id: vp.TransferFundId;
  require: boolean;
  preset: keyof vp.TextFieldPresets | undefined;
  type: 'input' | 'select' | 'singleDate' | 'multiSelectDropdown' | 'multiSelector';
  placeholder: string;
  visible: (ctx: TransferFundRequest) => boolean;
  maxLength?: number;
  disableFiltering?: () => boolean;
  onSearch?: (term: string) => void;
  getSelectOptions?: () => { [key: string]: string };
  customOptions?: SelectOption[];
  getMultiSelectOptions?: (value: string) => SelectOption[];
  getMultiSelectValues?: (value: string) => SelectOption[];
  disabled?: boolean;
  onChangeCallBack?: (
    event: ChangeEvent<HTMLInputElement>,
    ctx: TransferFundRequest,
    setFieldValue: FormikHelpers<TransferFundRequest>['setFieldValue'],
    setFieldTouched: FormikHelpers<TransferFundRequest>['setFieldTouched'],
  ) => void;
  getSubElement?: (ctx: TransferFundRequest) => JSX.Element | undefined;
  renderOption?: (o: vp.ClientAccount, args: { focused: boolean }) => React.ReactNode; // TODO: handle focused state manually
  renderMultiselectLabel?: (o: vp.ClientAccount, args: { selected: boolean; focused: boolean }) => React.ReactNode;
  SelectLabel?: (ctx: TransferFundRequest) => string | undefined;
  onMultiselect?: (items: SelectOption[]) => void;
};

export const buildTransferFundRows = (
  clientAccounts: { [idx: number]: vp.ClientAccount },
  recipientclientAccounts: { [idx: number]: vp.ClientAccount },
  currency: string,
  transactionLabelOverrideEnabled: string,
  country: string,
  USDEFTSendEnabled: string,
  fetchDebitorListWithSearchTerm: (searchTerm: string | undefined) => void,
  setDebitorDetail: (details: vp.ClientAccount) => void,
  setRecipientDetail: (details: vp.ClientAccount) => void,
  debitorDetail: vp.ClientAccount | undefined,
  recipientDetail: vp.ClientAccount | undefined,
): TransferFundRows[] => {
  const clientAccountOptions = buildClientAccountOptions(clientAccounts);
  const recipientClientAccountOptions = buildClientAccountOptions(recipientclientAccounts);
  const [recipientClientAccounts, setRecipientClientAccounts] = useState<SelectOption[]>();
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds.Form' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const CurrencyOptions = {
    ...vp.CURRENCY_SELECT_OPTIONS,
  };

  const CADOnly: { [key: string]: string } = {
    CAD: 'CAD',
  };

  const TransferTypeOptions: { [key: string]: string } = {
    Transfer: tGlobal('TransferTypeOptions.transfer'),
    FundAndTransfer: tGlobal('TransferTypeOptions.fundTransfer'),
    TransferAndWithdraw: tGlobal('TransferTypeOptions.transferWithdraw'),
    FundAndTransferAndWithdraw: tGlobal('TransferTypeOptions.fundTransferWithdraw'),
  };

  const FrequencyOptions = {
    monthly: tGlobal('FrequencyOptions.monthly'),
    weekly: tGlobal('FrequencyOptions.weekly'),
    'semi-monthly': tGlobal('FrequencyOptions.semiMonthly'),
    yearly: tGlobal('FrequencyOptions.annually'),
  };

  const ScheduleTypeOptions: { [key: string]: string } = {
    single: tGlobal('ScheduleType.single'),
    recurring: tGlobal('ScheduleType.recurring'),
  };

  const EndingOptions: { [key: string]: string } = {
    'End Date': tGlobal('EndingOptions.endDate'),
    Payments: tGlobal('EndingOptions.payments'),
  };

  const SemiMonthlyDateOptions = {
    SemiMonthFirstAndFifteenth: tGlobal('SemiMonthlyDateOptions.firstAndFifteenth'),
    SemiMonthFifteenthAndLast: tGlobal('SemiMonthlyDateOptions.fifteenthAndLast'),
  };

  return [
    {
      label: t('currencyLabel'),
      id: vp.TransferFundId.Currency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: () => true,
      getSelectOptions: () =>
        country === 'US' ? vp.US_CURRENCY_ONLY : USDEFTSendEnabled === '0' ? CADOnly : CurrencyOptions,
    },
    {
      label: t('TransferType.label'),
      id: vp.TransferFundId.TransferType,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('TransferType.placeholder'),
      visible: () => true,
      getSelectOptions: () => TransferTypeOptions,
      onChangeCallBack: (event, _ctx: TransferFundRequest, setFieldValue, setFieldTouched) => {
        const TransferType = event.target.value;

        if (TransferType === 'Transfer') {
          setFieldValue('RecipientClientAccountID', '');
          setFieldTouched('RecipientClientAccountID', false);
          setFieldValue('RecipientClientAccountIDSplit', '');
          setFieldTouched('RecipientClientAccountIDSplit', false);
        }
      },
    },
    {
      label: t('DebitorClientAccountID.label'),
      id: vp.TransferFundId.DebitorClientAccountID,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('DebitorClientAccountID.placeholder'),
      visible: () => true,
      customOptions: clientAccountOptions,
      renderOption: (o, args) => (
        <OptionLabel clientName={o.ClientName} currency={currency} balanceAccount={getBalanceAccount(o)} {...args} />
      ),
      disableFiltering: () => true,
      onSearch: (value: string) => {
        fetchDebitorListWithSearchTerm(value === '' ? undefined : value);
      },
      onChangeCallBack: (event) => {
        const DebitorClientAccountID = event.target.value;

        const clientAccount = Object.values(clientAccounts).find(
          (clientAccounts) => clientAccounts.ClientAccountID === DebitorClientAccountID,
        );

        if (clientAccount) {
          setDebitorDetail(clientAccount);
          debitorDetail = clientAccount;
        }
      },
      getSubElement: (ctx: TransferFundRequest) => {
        const clientAccountBalance = Object.values(debitorDetail?.Balances || {}).find(
          (balance) => balance.Currency === ctx.Currency,
        );

        return ctx.DebitorClientAccountID !== '' && debitorDetail ? (
          <div className={styles.AvailableBalance}>
            {clientAccountBalance && (
              <span>
                {t('DebitorClientAccountID.fieldPrefix')} {ctx.Currency}{' '}
                {vp.formattedCurrency((clientAccountBalance.Balance - clientAccountBalance.PendingBalance).toString())}
              </span>
            )}
            {!clientAccountBalance && (
              <span>
                {t('DebitorClientAccountID.fieldPrefix')} {ctx.Currency} {vp.formattedCurrency('0')}
              </span>
            )}
          </div>
        ) : undefined;
      },
      SelectLabel: () => {
        return debitorDetail?.ClientName;
      },
    },
    {
      label: t('RecipientClientAccountID.label'),
      id: vp.TransferFundId.RecipientClientAccountID,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: t('RecipientClientAccountID.placeholder'),
      visible: (ctx: TransferFundRequest) => {
        if (ctx.TransferType === 'Transfer') {
          return true;
        }
        return false;
      },
      customOptions: recipientClientAccountOptions,
      renderOption: (o, args) => (
        <OptionLabel clientName={o.ClientName} currency={currency} balanceAccount={getBalanceAccount(o)} {...args} />
      ),
      onChangeCallBack: (event) => {
        const RecipientClientAccountID = event.target.value;

        const clientAccount = Object.values(recipientclientAccounts).find(
          (clientAccounts) => clientAccounts.ClientAccountID === RecipientClientAccountID,
        );

        if (clientAccount) {
          setRecipientDetail(clientAccount);
          recipientDetail = clientAccount;
        }
      },
      getSubElement: (ctx: TransferFundRequest) => {
        const clientAccountBalance = Object.values(recipientDetail?.Balances || {}).find(
          (balance) => balance.Currency === ctx.Currency,
        );
        return recipientDetail ? (
          <div className={styles.AvailableBalance}>
            {clientAccountBalance && (
              <span>
                {t('RecipientClientAccountID.fieldPrefix')} {ctx.Currency}{' '}
                {vp.formattedCurrency((clientAccountBalance.Balance - clientAccountBalance.PendingBalance).toString())}
              </span>
            )}
            {!clientAccountBalance && (
              <span>
                {t('RecipientClientAccountID.fieldPrefix')} {ctx.Currency} {vp.formattedCurrency('0')}
              </span>
            )}
          </div>
        ) : undefined;
      },
      disableFiltering: () => true,
    },
    {
      label: t('RecipientClientAccountID.label'),
      id: vp.TransferFundId.RecipientClientAccountID,
      require: true,
      preset: 'text',
      type: 'multiSelector',
      placeholder: t('RecipientClientAccountID.placeholder'),
      visible: (ctx: TransferFundRequest) => {
        if (ctx.TransferType !== 'Transfer') {
          return true;
        }

        return false;
      },
      onMultiselect: (items) => {
        setRecipientClientAccounts(items);
      },
      getMultiSelectValues: () => {
        return recipientClientAccounts || [];
      },
      getMultiSelectOptions: () => {
        const outputArray: SelectOption[] = recipientClientAccountOptions.map((opt) => ({
          ...opt,
          label: opt.ClientName,
          value: opt.ClientAccountID,
        }));
        return outputArray || [];
      },
      renderMultiselectLabel: (o, args) => (
        <MultiSelectOptionLabel
          clientName={o.ClientName}
          currency={currency}
          balanceAccount={getBalanceAccount(o)}
          {...args}
        />
      ),
      getSubElement: (ctx: TransferFundRequest) => {
        if (ctx.RecipientClientAccountID.split('|').length > 1) return undefined;
        const clientAccount = Object.values(clientAccounts).find(
          (clientAccountIteration) => clientAccountIteration.ClientAccountID === ctx.RecipientClientAccountID,
        );
        const clientAccountBalance = Object.values(clientAccount?.Balances || {}).find(
          (balance) => balance.Currency === ctx.Currency,
        );
        return clientAccount ? (
          <div className={styles.AvailableBalance}>
            {clientAccountBalance && (
              <span>
                {t('RecipientClientAccountID.fieldPrefix')} {ctx.Currency}{' '}
                {vp.formattedCurrency((clientAccountBalance.Balance - clientAccountBalance.PendingBalance).toString())}
              </span>
            )}
            {!clientAccountBalance && (
              <span>
                {t('RecipientClientAccountID.fieldPrefix')} {ctx.Currency} {vp.formattedCurrency('0')}
              </span>
            )}
          </div>
        ) : undefined;
      },
      disabled: (() => Object.values(clientAccounts || {}).length === 0)(),
    },
    {
      label: t('amountLabel'),
      id: vp.TransferFundId.Amount,
      require: true,
      preset: 'currency',
      type: 'input',
      placeholder: '',
      visible: () => true,
    },
    {
      label: t('splitMethodLabel'),
      id: vp.TransferFundId.SplitMethod,
      require: true,
      preset: 'text',
      type: 'multiSelectDropdown',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => {
        let showSplitMethod = false;
        if (ctx.RecipientClientAccountID !== '' && ctx.TransferType !== '' && ctx.TransferType !== 'Transfer') {
          showSplitMethod = ctx.RecipientClientAccountID.split('|').length > 1;
        }
        return showSplitMethod;
      },
      getSelectOptions: () => ({}),
    },
    {
      label: t('scheduleTypeLabel'),
      id: vp.TransferFundId.ScheduleType,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer',
      getSelectOptions: () => ScheduleTypeOptions,
    },
    {
      label: t('TakeFromAccountOn.label'),
      id: vp.TransferFundId.TakeFromAccountOn,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('TakeFromAccountOn.placeholder'),
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'single',
    },
    {
      label: t('StartDate.label'),
      id: vp.TransferFundId.StartDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('StartDate.placeholder'),
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring',
    },
    {
      label: t('frequencyLabel'),
      id: vp.TransferFundId.Frequency,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring',
      getSelectOptions: () => FrequencyOptions,
    },
    {
      label: t('semiMonthlyDatesLabel'),
      id: vp.TransferFundId.SemiMonthlyDates,
      require: true,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) =>
        ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring' && ctx.Frequency === 'semi-monthly',
      getSelectOptions: () => SemiMonthlyDateOptions,
    },
    {
      label: t('endingLabel'),
      id: vp.TransferFundId.Ending,
      require: false,
      preset: 'text',
      type: 'select',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.TransferType !== 'Transfer' && ctx.ScheduleType === 'recurring',
      getSelectOptions: () => EndingOptions,
    },
    {
      label: t('EndDate.label'),
      id: vp.TransferFundId.EndDate,
      require: true,
      preset: 'date',
      type: 'singleDate',
      placeholder: t('EndDate.placeholder'),
      visible: (ctx: TransferFundRequest) => ctx.ScheduleType === 'recurring' && ctx.Ending === 'End Date',
    },
    {
      label: t('endingAfterPaymentsLabel'),
      id: vp.TransferFundId.EndingAfterPayments,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: (ctx: TransferFundRequest) => ctx.Ending === 'Payments',
    },
    {
      label: t('Description.label'),
      id: vp.TransferFundId.Description,
      require: true,
      preset: 'text',
      type: 'input',
      placeholder: t('Description.placeholder'),
      visible: (ctx: TransferFundRequest) =>
        ctx.TransferType !== 'Transfer' &&
        (ctx.ScheduleType === 'recurring' ||
          (ctx.TakeFromAccountOn !== '' &&
            !vp.IsTodayDate(moment(Number(ctx.TakeFromAccountOn)).format('YYYY-MM-DD')))),
    },
    {
      label: t('clientReferenceNumberLabel'),
      id: vp.TransferFundId.ClientReferenceNumber,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
    },
    {
      label: t('transactionLabel'),
      id: vp.TransferFundId.TransactionLabel,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => transactionLabelOverrideEnabled === '1',
      maxLength: 30,
    },
    {
      label: t('notesLabel'),
      id: vp.TransferFundId.Notes,
      require: false,
      preset: 'text',
      type: 'input',
      placeholder: '',
      visible: () => true,
    },
  ];

  function buildClientAccountOptions(clientAccountList: { [idx: number]: vp.ClientAccount }) {
    const lists = Object.values(clientAccountList);
    const options: SelectOption[] = [];
    /*
     * forEach was used instead of reduce because when the list got large around 30000 client
     * accounts the overhead of maintaining the accumulator caused significant performance issues
     * when compared to forEach
     */
    lists.forEach((data) => {
      if (data.IsActive) {
        options.push({
          ...data,
          label: data.ClientName,
          value: data.ClientAccountID,
        });
      }
    });
    return options;
  }

  function getBalanceAccount(data: vp.ClientAccount) {
    const balanceAccountObject = Object.values(data.Balances || {}).find((balance) => balance.Currency === currency);
    return (balanceAccountObject?.Balance || 0) - (balanceAccountObject?.PendingBalance || 0);
  }
};

function OptionLabel({
  clientName,
  currency,
  balanceAccount,
  focused,
}: {
  clientName: string;
  currency: string;
  balanceAccount: number;
  focused: boolean;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds.Form' });
  return (
    <div className={[styles.optionLabel, focused ? styles.focused : ''].join(' ')}>
      <div className={styles.optionLabelContent}>
        <div>{clientName}</div>
        <div className={styles.formatAvailableBalance}>
          {t('optionLabelPrefix', { currency, amount: vp.formattedCurrency(balanceAccount.toString()) })}
        </div>
      </div>
    </div>
  );
}

function MultiSelectOptionLabel({
  clientName,
  currency,
  balanceAccount,
  selected,
  focused,
}: {
  clientName: string;
  currency: string;
  balanceAccount: number;
  selected: boolean;
  focused: boolean;
}) {
  const { t } = useTranslation('translations', { keyPrefix: 'ClientAccounts.TransferFunds.Form' });
  return (
    <div className={[styles.optionLabel, focused ? styles.focused : ''].join(' ')}>
      <div className={styles.optionLabelContent}>
        <div className={styles.optionLabelTop}>
          {selected && <vp.Icon icon="check" color="primary-dark" width="12px" height="10px" size="sm" />}
          <div>{clientName}</div>
        </div>
        <div className={styles.formatAvailableBalance}>
          {t('optionLabelPrefix', { currency, amount: vp.formattedCurrency(balanceAccount.toString()) })}
        </div>
      </div>
    </div>
  );
}
